import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = user
  const [error, setError] = useState("");
  const { signOut: clerkSignOut, isLoaded: clerkLoaded, isSignedIn } = useClerkAuth();

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!clerkLoaded) return;
    if (!isSignedIn) {
      refreshMe();
    }
  }, [clerkLoaded, isSignedIn, refreshMe]);

  const syncClerkSession = async (sessionToken) => {
    setError("");
    try {
      const { data } = await api.post("/auth/clerk", { session_token: sessionToken });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e) || "Connexion impossible.";
      setError(msg);
      throw new Error(msg);
    }
  };

  const clearFaanuuSession = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  const logout = async () => {
    await clearFaanuuSession();
    try {
      if (clerkSignOut) await clerkSignOut();
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        logout,
        refreshMe,
        syncClerkSession,
        clearFaanuuSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
